import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/**
 * Left-navigation menu, served from the tenant's own database.
 *
 * The sidebar renders whatever comes back here. If the call fails, or the
 * tenant's table has not been seeded yet, the Sidebar falls back to its
 * bundled tree — navigation is the one surface that must never be empty.
 */

export type NavMenuNode = {
  id: string;
  item_key: string;
  label: string;
  /** SPA route relative to APP_BASE. `null` = container node; `""` is a
   *  real destination (the SPA index route the Dashboard sits on). */
  route: string | null;
  icon: string;
  badge: 'new' | 'soon' | string | null;
  /** Container nodes: render as an expandable dropdown. */
  is_collapsible: boolean;
  sort_order: number;
  allowed_roles: string[];
  is_active: boolean;
  is_system: boolean;
  entity_id: string | null;
  menu_scope: string;
  children: NavMenuNode[];
};

export type NavMenu = {
  scope: string;
  role: string;
  items: NavMenuNode[];
  /** Routes of the staff pages the user's roles cannot open; the shell refuses them and anything below them. */
  denied?: string[];
};

/** `enabled`: false before sign-in and for self-service users, who never open a staff page. */
export function useNavMenu(entityId?: string | null, enabled = true) {
  return useQuery({
    enabled,
    // A company switch keeps the last menu until the new one answers, so the shell never unmounts the open page.
    placeholderData: keepPreviousData,
    queryKey: ['nav', 'menu', entityId || null],
    queryFn: () => api.get<NavMenu>('/nav/menu', {
      entity_id: entityId || undefined,
    }),
    // The menu changes when an admin edits it, which is rare. Keep it warm so
    // navigating between pages never re-fetches and flickers the sidebar.
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

/** A row of GET /nav/menu/items: every row of both menus, flat, entity overrides and inactive rows included. */
export type MenuItemRow = {
  id: string;
  item_key: string;
  parent_id: string | null;
  /** Set on an entity's own override of a tenant-wide row. */
  entity_id: string | null;
  label: string;
  route: string | null;
  sort_order: number;
  menu_scope: 'STAFF' | 'ESS';
  /** Who sees an item a tenant added; empty means everyone. Shipped items follow `needs` instead. */
  allowed_roles: string[];
  is_active: boolean;
  is_system: boolean;
  /** The permissions that open a shipped item's page: it shows to whoever holds any. Empty for a section or a
   *  tenant's own item. */
  needs: string[];
};

export function menuItemsQuery() {
  return queryOptions({
    queryKey: ['nav', 'menu-items'],
    queryFn: () => api.get<MenuItemRow[]>('/nav/menu/items'),
  });
}

/** `enabled`: false for a reader without settings.menu.view, who would be refused. */
export function useMenuItems(enabled = true) {
  return useQuery({ ...menuItemsQuery(), enabled });
}
